import { Divider } from "antd";
import { useCallback, useState } from "react";
import { ContentButtonStyled, ContentPostStyled, ContentTextCopy, DescriptionStyled, NoteStyled } from "../../style-components";
import { Tab1Props } from "./tab-1-types";
import { InfoCircleOutlined } from "@ant-design/icons";
import CustomButton from "@/components/custom-button/custom-button";
import { useTheme } from "@/contexts/theme-context/useThemeContext";
import CustomTooltip from "@/components/tooltip/tooltip";
import { facebookTooltips, xTwitterTooltips } from "./privacy-tooltips";
import { TooltipItem } from "@/components/tooltip/tooltip-types";
import postExampleImage from "./tooltip-assets/post-example.jpg"
import { useUserContext } from "@/contexts/user/useUserContext";
import ButtonCopy from "./components/button-copy/button-copy";
import AlreadyParticipated from "./components/already-participated/already-participated";
import ModalsProcess from "./components/modals-process/modals-process";

const ALLOWED_LINKS: { [key: string]: string } = {
  TWITTER: 'https://twitter.com/intent/tweet?',
  XTWITTER: 'https://twitter.com/intent/tweet?',
  FACEBOOK: 'https://www.facebook.com/',
  LINKEDIN: 'https://www.linkedin.com/shareArticle?',
}

const TOOLTIPS: { [key: string]: TooltipItem[] } = {
  TWITTER: xTwitterTooltips,
  XTWITTER: xTwitterTooltips,
  FACEBOOK: facebookTooltips,
}

const Tab1: React.FC<Tab1Props> = ({
  description,
  hashtags,
  participant,
  template,
  isExpiredMission,
  isComingSoon,
  completedRequirements,
  position,
  loadingProgress,
  hookObjectivePost,
  disabledMission,
}) => {
  const { socialMediaSelected, postSaved, loading, isSavePost } = hookObjectivePost;
  const { isConnect } = useUserContext();
  const theme = useTheme();
  const idExternal = Array.isArray(participant) && participant.length > 0 ? participant[0].user.reference_id : null;
  const socialUserParticipant = Array.isArray(participant) && participant.length > 0 ? participant[0]?.social_info?.username : null;
  const [modalSaveUsername, setModalSaveUsername] = useState<boolean>(false);
  const [modalConfirm, setModalConfirm] = useState<boolean>(false);
  const [modalSaveUrlPost, setModalSaveUrlPost] = useState<boolean>(false);
  const [showScanPost, setShowScanPost] = useState<boolean>(false);
  const nameSocialMedia = String(socialMediaSelected?.name).toUpperCase();
  const url = ALLOWED_LINKS[nameSocialMedia as keyof typeof ALLOWED_LINKS] ?? null;
  const changePrivacyTooltips = TOOLTIPS[nameSocialMedia as keyof typeof TOOLTIPS] ?? [{message:"No tooltip found for selected social media", illustration:""}];
  const loadingGlobal = loadingProgress || loading;
  const disableButtons = loadingGlobal || completedRequirements && !socialMediaSelected?.name || isExpiredMission || isComingSoon || !isConnect || disabledMission;

  const handleGoToSocialMedia = useCallback(() => {
    if (!url) return;
    if (nameSocialMedia === 'XTWITTER' || nameSocialMedia === 'TWITTER') {
      const text = `${template ?? ''} ${idExternal ?? ''}`;
      const encodedText = encodeURIComponent(text);
      window.open(`${url}text=${encodedText}`, '_blank');
    } else {
      window.open(url, '_blank');
    }
  }, [url, nameSocialMedia, template, idExternal]);

  const handleContinue = useCallback(() => {
    setModalSaveUsername(false);
    setModalConfirm(false);
    setShowScanPost(true);
  },[]);

  const handleClickAlreadyPosted = useCallback(() => {
    if (isSavePost) {
      setShowScanPost(true);
    }else {
      setModalSaveUsername(true);
    }
  },[isSavePost]);

  return (
    <ContentPostStyled $disabledMission={disabledMission}>
      <DescriptionStyled>
        <p style={{ margin: 0 }} dangerouslySetInnerHTML={{ __html: `<span class='number'>${position}.</span> ${description}` }} />
        <CustomTooltip items={[{ message: "Tu publicación debe verse algo así.", illustration: <img src={postExampleImage} alt="Post example" /> }]}>
          <InfoCircleOutlined style={{ color: theme.theme.colors.theme.brandColor3 }} />
        </CustomTooltip>
      </DescriptionStyled>
      <ContentTextCopy>
        <ButtonCopy
          hashtags={hashtags}
          idExternal={idExternal}
          template={template}
          socialUserParticipant={socialUserParticipant}
          isComingSoon={isComingSoon}
          isConnect={isConnect}
        />
      </ContentTextCopy>
      <Divider style={{ margin: '0px', borderColor: theme?.theme?.colors?.theme?.contentBgOpaque }} />
      {showScanPost ? 
      <AlreadyParticipated 
        postSaved={postSaved} 
        nameSocialMedia={nameSocialMedia}
        loading={loading}
        isExpiredMission={isExpiredMission} 
        isComingSoon={isComingSoon}
        isConnect={isConnect}
        setModalSaveUrlPost={setModalSaveUrlPost}
      /> :
      <ContentButtonStyled>
        <CustomButton
          type="primary"
          disabled={disableButtons}
          onClick={handleGoToSocialMedia}
          style={{ padding: '10px 24px', borderRadius: '40px', height: '40px' }}>
          {socialMediaSelected?.name ? `Publicar en ${socialMediaSelected.name}` : 'Red social no disponible'}
        </CustomButton>
        <CustomButton
          color="white"
          variant="outlined"
          disabled={disableButtons}
          onClick={handleClickAlreadyPosted}
          data-testid="button-already-posted"
          style={{ padding: '10px 24px', borderRadius: '40px', height: '40px' }}>
          Ya publiqué
        </CustomButton>
      </ContentButtonStyled>}
      {!isSavePost && (
        <CustomTooltip items={changePrivacyTooltips}>
          <NoteStyled data-testid="change-privacy-tooltip-element">
            <InfoCircleOutlined style={{color: theme.theme.colors.theme.info}}/>
            <p>
              Haz tu publicación visible para todos.
            </p>
          </NoteStyled>
        </CustomTooltip>
      )}
      <ModalsProcess 
        modalSaveUsername={modalSaveUsername}
        setModalSaveUsername={setModalSaveUsername}
        modalConfirm={modalConfirm}
        setModalConfirm={setModalConfirm}
        modalSaveUrlPost={modalSaveUrlPost}
        setModalSaveUrlPost={setModalSaveUrlPost}
        handleContinue={handleContinue}
        hookObjectivePost={hookObjectivePost}
      />
    </ContentPostStyled>
  )
};

export default Tab1;